import { createContext, ReactNode, useContext } from 'react';

/**
 * Widget-vs-marketplace seam for the booking-flow chrome: the header's left
 * slot on an unstamped entry, and where the flow bails out when its place never
 * arrives. The booking widget mounts the same step pages as the marketplace, so
 * there is no prop seam to pass these through — the widget's host supplies them
 * via this context instead, and the step pages never learn about embedding.
 *
 * The default (no provider) reproduces today's marketplace behaviour exactly:
 * stamp-or-Avbryt in the header, place-page bail-out. Only the widget's
 * WidgetHost renders a provider (same default-value pattern as
 * AmplitudeExperimentContext).
 */

/** Header left slot on an unstamped entry. `null` means render no slot at all. */
export type BookingFlowChromeLeftSlot = { label: string; onBack: () => void } | null;

export type BookingFlowChromeValue = {
  /**
   * Left-slot override for entries carrying no goToStep stamp. `undefined`
   * keeps the marketplace "Avbryt to the place page"; `null` renders no slot;
   * an object renders that label and invokes that handler. A stamped entry
   * always wins over this, so deeper steps rewind through history everywhere.
   */
  leftSlot?: BookingFlowChromeLeftSlot;
  /**
   * Pathname the flow bails out to when the place never arrives
   * (useBookingFlowSelection's place-wait timeout). `undefined` keeps the
   * marketplace place-page target. The query string is preserved either way.
   */
  exitTo?: string;
  /**
   * Pathname standing in for the place page, which the widget does not host,
   * wherever the flow sends the visitor there to start over — the calendar's
   * no-times "choose another service". `undefined` keeps the marketplace place
   * page. The query string is preserved either way.
   */
  placePageTo?: (placeSlugId: string) => string;
  /**
   * The host's attribution block, which each screen places where its own layout
   * has room for it: `bar` spans the screen's width, `column` sits inside a side
   * column. `undefined` renders none — the marketplace carries no attribution.
   */
  attribution?: (variant: 'bar' | 'column') => ReactNode;
  /**
   * The merchant opted out of its logo in the header's right slot: every
   * CheckoutHeader under the provider renders that slot empty, whatever the
   * page passes it. `undefined` keeps the logo.
   */
  hideLogo?: boolean;
};

export const BookingFlowChromeContext = createContext<BookingFlowChromeValue>({});

export const useBookingFlowChrome = (): BookingFlowChromeValue => useContext(BookingFlowChromeContext);
